import type { Article } from 'interfaces/content/articles/Articles';
import type { SubscriberContext } from 'services/Subscriber';
import type { EventData, OptimizelyDecisionTracking } from 'services/Gtm/entities';
import { trackEvent } from 'services/Gtm/functions';
import { PageType } from 'interfaces/content/articles/Post';
import type CustomWindow from 'interfaces/Utils';
import CookieService from './miscUtils/CookieService';

declare let window: CustomWindow;

export const getArticleTrackingData = (article: Article, user: SubscriberContext, subdomain: string) => {
  const hasFeaturedMediaTypeVideo =
    article.featuredMediaType === 'fortune_video' || article.featuredMediaType === 'stn_video_media';
  const optimizelyData =
    typeof window !== 'undefined'
      ? window?.dataLayer?.filter((item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision')
      : [];
  const trackingData: EventData = {
    HasVideoCD: hasFeaturedMediaTypeVideo,
    articleNameCD: article.title,
    authorNameCD: article.authorNames,
    cmsCategoryCD: 'article',
    cmsSubCategoryCD: article.primarySection.name,
    contentCMSID: article.postId,
    contentPlacementCD: `${subdomain} Widget`,
    contentPublishedDateCD: article.dateGmt,
    contentTagsCD: article.tagNames,
    hitIDCD: '',
    loginStatusCD: user.isLoggedIn ? 'logged-in' : 'guest',
    optimizely: optimizelyData,
    platformCD: article.platformCD || 'own',
    premiumCategoryCD: article?.premiumCategory.name || '',
    sessionIDCD: '',
    userAgentCD: typeof window !== 'undefined' ? window.navigator?.userAgent : '',
  };

  return { trackingData };
};

export const trackInlineButtonClick = (event: Event, defaultTrackingData: EventData, subdomain: string) => {
  const trackingData = {
    ...defaultTrackingData,
    contentPlacementCD: `Inline ${subdomain} Button`,
    eventAction: 'inline click',
    eventCategory: `Inline ${subdomain} Button`,
    eventLabel: (event.target as HTMLAnchorElement).textContent || '',
  };

  trackEvent(trackingData);
};

export const trackArticleLinkClick = (event: Event, defaultTrackingData: EventData) => {
  const target: HTMLAnchorElement = event.target as HTMLAnchorElement;
  // the anchor element that was clicked could be a wrapper of another element (e.g. image)
  // in this case the target might not be the anchor itself
  const articleUrl = target.tagName === 'A' ? target.href : target?.closest('a')?.href;
  if (!articleUrl) return;
  let urlDomain;
  try {
    urlDomain = new URL(articleUrl).origin;
  } catch {
    return;
  }
  const isExternalURL = urlDomain !== window.location.origin;
  if (!isExternalURL) return;

  const trackingData = {
    ...defaultTrackingData,
    contentPlacementCD: 'Article Body',
    eventAction: urlDomain,
    eventCategory: 'Outbound Link Click',
    eventLabel: articleUrl,
  };

  trackEvent(trackingData);
};

export const setEventAction = (eyebrowSectionLabel: string, eyebrowTagLabel: string): string => {
  let eventActionContent;

  if (
    eyebrowSectionLabel !== 'No Primary Section Found' &&
    eyebrowTagLabel !== 'No Primary Tag Found' &&
    eyebrowTagLabel !== ''
  ) {
    eventActionContent = `${eyebrowSectionLabel} : ${eyebrowTagLabel}`;
  } else if (eyebrowSectionLabel !== 'No Primary Section Found') {
    eventActionContent = `${eyebrowSectionLabel}`;
  } else {
    eventActionContent = 'Not Set';
  }

  return eventActionContent;
};

export const setPrefixedDbID = (ID: string | number, pageType?: PageType): string => {
  switch (pageType) {
    case PageType.EDU_CATEGORY:
      return `C${ID}`;
    case PageType.EDU_RANKING_LIST:
      return `R${ID}`;
    case PageType.EDU_SCHOOL:
      return `S${ID}`;
    case PageType.FRANCHISE_LIST:
      return `fl_${ID}`;
    case PageType.COMPANY:
      return `co_${ID}`;
    default:
      return `${ID}`;
  }
};

export const getSessionIdentifiers = () => {
  try {
    const { tp } = window;
    const user = tp?.pianoId?.getUser();
    const sessionIdentifiers = {
      fuid: CookieService.get('fuid') || '',
      ga_client_id: CookieService.get('_ga')?.slice(6) || '',
      piano_user_id: user?.uid || '',
    };

    return sessionIdentifiers;
  } catch {
    return {
      fuid: '',
      ga_client_id: '',
      piano_user_id: '',
    };
  }
};
